/**
 * Per-field runtime plate-type selection in Native Booking draft.
 * Keep in sync with apps/api/src/native-booking/domain/native-plate-format-choice.ts
 */

import { documentIdentityKey } from './native-booking-guards';
import {
  isRuPlateRuntimeSelection,
  type RuPlateRuntimeSelection,
} from './ru-plate-formats';

export const NATIVE_PLATE_FORMATS_PAYLOAD_KEY = '__native_plate_formats__';

/** Prefix distinguishing document-metadata selections from ordinary field_key entries. */
export const DOCUMENT_META_PLATE_SELECTION_PREFIX = 'doc:';

export type NativePlateFormatChoiceMap = Readonly<Record<string, RuPlateRuntimeSelection>>;

/**
 * Collision-safe map key for a document metadata plate selection.
 * Shape: `doc:{documentTypeId}::{subjectBinding}:{fieldCode}`
 */
export function documentMetadataPlateSelectionKey(
  documentTypeId: string,
  subjectBinding: string,
  fieldCode: string,
): string {
  return `${DOCUMENT_META_PLATE_SELECTION_PREFIX}${documentIdentityKey(documentTypeId, subjectBinding)}:${fieldCode}`;
}

export function isDocumentMetadataPlateSelectionKey(key: string): boolean {
  return key.startsWith(DOCUMENT_META_PLATE_SELECTION_PREFIX);
}

export function readNativePlateFormatChoices(
  draft: Record<string, unknown> | null | undefined,
): NativePlateFormatChoiceMap {
  if (draft === null || draft === undefined) {
    return {};
  }
  const raw = draft[NATIVE_PLATE_FORMATS_PAYLOAD_KEY];
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const out: Record<string, RuPlateRuntimeSelection> = {};
  for (const [fieldKey, value] of Object.entries(raw as Record<string, unknown>)) {
    if (fieldKey.trim() !== '' && isRuPlateRuntimeSelection(value)) {
      out[fieldKey] = value;
    }
  }
  return out;
}

/**
 * Prefer namespaced document key; legacy bare field_code only when namespaced absent.
 */
export function readDocumentMetaPlateSelection(
  draft: Record<string, unknown> | null | undefined,
  documentTypeId: string,
  subjectBinding: string,
  fieldCode: string,
): RuPlateRuntimeSelection | undefined {
  const choices = readNativePlateFormatChoices(draft);
  const namespaced = documentMetadataPlateSelectionKey(documentTypeId, subjectBinding, fieldCode);
  if (Object.prototype.hasOwnProperty.call(choices, namespaced)) {
    return choices[namespaced];
  }
  if (Object.prototype.hasOwnProperty.call(choices, fieldCode)) {
    return choices[fieldCode];
  }
  return undefined;
}

export function withNativePlateFormatChoice(
  draft: Record<string, unknown>,
  fieldKey: string,
  selection: RuPlateRuntimeSelection | null,
): Record<string, unknown> {
  const current = { ...readNativePlateFormatChoices(draft) };
  if (selection === null) {
    delete current[fieldKey];
  } else {
    current[fieldKey] = selection;
  }
  if (Object.keys(current).length === 0) {
    const next = { ...draft };
    delete next[NATIVE_PLATE_FORMATS_PAYLOAD_KEY];
    return next;
  }
  return {
    ...draft,
    [NATIVE_PLATE_FORMATS_PAYLOAD_KEY]: current,
  };
}
